<template>
  <div class="step-one-single" v-loading="loading">
    <!-- 配置车型信息 -->
    <GlobalCarInfo
      ref="globalCarInfoRef"
      title="配置车型信息"
      :showAlert="false"
      :syncToStore="false"
    />

    <!-- 租赁方案信息（只读） -->
    <div class="section-card" v-if="planDetail">
      <div class="section-title">租赁方案信息</div>
      <div class="plan-info-list">
        <div class="info-row">
          <span class="label">方案名称</span>
          <span class="value">{{ planDetail.name }}</span>
        </div>
        <div class="info-row">
          <span class="label">车型年款</span>
          <span class="value">{{ planDetail.modelName }}</span>
        </div>
        <div class="info-row">
          <span class="label">城市</span>
          <span class="value" v-if="planDetail.cityCount <= 5">
            {{ planDetail.cities }}
          </span>
          <el-tooltip v-else placement="top" :content="planDetail.cities">
            <span class="value cities-ellipsis">
              {{ planDetail.citiesDisplay }}
            </span>
          </el-tooltip>
        </div>
        <div class="info-row">
          <span class="label">租期</span>
          <span class="value">{{ planDetail.period }}</span>
        </div>
        <div class="info-row">
          <span class="label">押金总额</span>
          <span class="value">{{ planDetail.deposit }}</span>
        </div>
        <div class="info-row">
          <span class="label">租金</span>
          <span class="value">{{ planDetail.rent }}</span>
        </div>
        <div class="info-row" v-if="planDetail.otherFees">
          <span class="label">其他费用</span>
          <span class="value">{{ planDetail.otherFees }}</span>
        </div>
        <div class="info-row">
          <span class="label">赠送天数</span>
          <span class="value">{{ planDetail.giftDays || 0 }}天</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { createBatchCache, setBatchCarConfig } from '@/api/goods'
import { getLeasePlanDetails } from '@/api/purchaseAndSaleAPI/index.js'
import { onMounted, ref } from 'vue'
import GlobalCarInfo from './components/GlobalCarInfo.vue'
import { useBatchSession } from './hooks/useBatchSession'

const {
  sessionId,
  planId,
  planDetail,
  setPlanDetail,
  setGlobalCarInfo,
} = useBatchSession()

const loading = ref(false)
const globalCarInfoRef = ref(null)

// 加载方案详情
const loadPlanDetail = async () => {
  if (!planId.value) return

  loading.value = true
  const res = await getLeasePlanDetails({ id: planId.value })
  loading.value = false

  if (res?.code === 1) {
    const { data } = res

    // 映射数据到组件需要的格式
    const cityNames = data.cityNames || []
    const detail = {
      id: data.id,
      name: data.name,
      modelId: data.modelId, // 保存 modelId，用于后续 createBatchCache 调用
      // 车型：品牌·车系 车型名称
      modelName:
        data.brandName && data.seriesName
          ? `${data.brandName}·${data.seriesName} ${data.modelName}`
          : data.modelName,
      // 城市列表
      cityCount: cityNames.length,
      cities: cityNames.join('、'),
      citiesDisplay:
        cityNames.length > 5
          ? `${cityNames.slice(0, 5).join('、')}...等${cityNames.length}个城市`
          : cityNames.join('、'),
      // 租期格式化
      period:
        data.rentPeriodType === 1
          ? `${data.rentPeriod}天`
          : `${data.rentPeriod}个月`,
      // 押金格式化
      deposit: data.deposit ? `${data.deposit}元` : '--',
      // 租金格式化
      rent: data.rentAmount
        ? `${data.rentAmount}元/${data.rentAmountUnit}`
        : '--',
      // 其他费用拼接
      otherFees:
        (data.otherFees || [])
          .map((fee) => `${fee.name} ${fee.payDetailString}`)
          .join('、') || '--',
      // 赠送天数
      giftDays: data.presentDays || 0,
    }

    setPlanDetail(detail)
  }
  // 失败时 axios 已显示错误，不需要额外处理
}

// 校验表单
const validate = () => {
  return globalCarInfoRef.value?.validate()
}

// 下一步钩子 - 单个方案模式在此时创建服务端缓存
const onNext = async () => {
  // 1. 获取表单数据并更新到 Vuex
  const formData = globalCarInfoRef.value?.getFormData()

  // 2. 数据完整性校验
  if (!formData) {
    throw new Error('请完善车型信息')
  }

  if (!planDetail.value?.modelId) {
    throw new Error('方案信息不完整，无法继续')
  }

  // 3. 更新全局车型信息到 Vuex
  setGlobalCarInfo({
    carAge: formData.carAge,
    mileage: formData.mileage,
    hasLicense: formData.hasLicense,
  })

  // 4. 创建服务端缓存（单租赁方案模式特有逻辑）
  const res = await createBatchCache({
    sessionId: sessionId.value,
    age: formData.carAge,
    carModelId: planDetail.value.modelId,
    planId: planId.value,
  })

  // 5. 检查缓存创建结果
  if (res?.code !== 1) {
    throw new Error(res?.msg || '创建缓存失败，请重试')
  }

  // 6. 发送表显里程和有无车证配置
  const configRes = await setBatchCarConfig({
    sessionId: sessionId.value,
    mileage: formData.mileage,
    carCert: formData.hasLicense ? 1 : 0,
  })

  // 7. 检查配置结果
  if (configRes?.code !== 1) {
    throw new Error(configRes?.msg || '配置车型信息失败，请重试')
  }
}

onMounted(() => {
  loadPlanDetail()
})

defineExpose({
  validate,
  onNext,
})
</script>

<style lang="scss" scoped>
.step-one-single {
  .plan-info-list {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .info-row {
      display: flex;
      align-items: flex-start;

      .label {
        width: 80px;
        flex-shrink: 0;
        color: #606266;
        font-size: 14px;
        line-height: 22px;
      }

      .value {
        flex: 1;
        color: #303133;
        font-size: 14px;
        line-height: 22px;
        word-break: break-all;

        &.cities-ellipsis {
          cursor: pointer;
        }
      }
    }
  }
}
</style>
